import { Breadcrumbs, Loader } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Button, ConfirmActionModal } from '../../../../components/Atoms';
import { useDeleteSecurityGroupMutate } from '../../../../hooks/CloudComputeHooks/useDeleteSecurityGroupMutate';
import useBreadcrumbItems from '../../../../hooks/useBreadcrumbItems';
import usePageHeading from '../../../../hooks/usePageHeading';
import SecurityGroupModal from '../CloudComputePage/components/SecurityGroupListTab/SecurityGroupModal';
import SecurityGroupRulesCard from './components/SecurityGroupRulesCard';
import { useSecurityGroupQuery } from './hooks/useSecurityGroupQuery';

export default function CloudComputeSecurityGroupPage() {
    const navigate = useNavigate();
    const { sgId } = useParams<'sgId'>();
    const { data: sg, isPending } = useSecurityGroupQuery(sgId);
    const deleteMutation = useDeleteSecurityGroupMutate();
    const { t } = useTranslation('cloudComputeSecurityGroup');
    const headingRef = usePageHeading(sg?.name, t('breadcrumbs.securityGroups'));

    const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure();
    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure();

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumbs.product'), href: '#' },
        { title: t('breadcrumbs.cloudCompute'), href: '/cloud-compute' },
        {
            title: t('breadcrumbs.securityGroups'),
            href: '/cloud-compute?tab=security-group',
        },
        { title: sg?.name ?? '...' },
    ]);

    const confirmDelete = () => {
        if (!sg) return;
        deleteMutation.mutate(sg.id, {
            onSuccess: () => {
                closeDelete();
                navigate('/cloud-compute');
            },
        });
    };

    if (isPending && !sg) {
        return (
            <div className="w-full p-8 flex justify-center">
                <Loader size={24} color="var(--fg)" />
            </div>
        );
    }

    if (!sg) {
        return (
            <div className="w-full p-8 text-center">
                <p className="text-base text-fg">{t('page.notFound')}</p>
                <p className="text-sm text-fg/68 mt-2">{t('page.notFoundDetail')}</p>
                <Button
                    className="mt-4"
                    variant="secondary"
                    onClick={() => navigate('/cloud-compute')}
                >
                    {t('page.backToCloudCompute')}
                </Button>
            </div>
        );
    }

    return (
        <div className="w-full">
            <Breadcrumbs separatorMargin={'xs'} className="mb-6">
                {breadcrumbItems}
            </Breadcrumbs>

            <div className="w-full flex flex-row justify-between items-start gap-4 mb-6">
                <div className="flex flex-col min-w-0 flex-1">
                    <h1
                        ref={headingRef}
                        tabIndex={-1}
                        className="text-2xl font-bold text-fg truncate"
                    >
                        {sg.name}
                    </h1>
                    {sg.description && <p className="text-sm text-fg/68 mt-1">{sg.description}</p>}
                    <p className="text-xs text-fg/68 mt-2 font-mono">
                        {t('page.ruleSummary', {
                            inbound: sg.in_rule_count,
                            outbound: sg.out_rule_count,
                        })}
                    </p>
                </div>
                <div className="flex flex-row items-center gap-2 shrink-0">
                    <Button
                        size="sm"
                        variant="secondary"
                        leftIcon={<IconPencil size={16} />}
                        onClick={openEdit}
                    >
                        {t('actions.edit')}
                    </Button>
                    <Button
                        size="sm"
                        variant="danger"
                        leftIcon={<IconTrash size={16} />}
                        onClick={openDelete}
                        disabled={deleteMutation.isPending}
                    >
                        {t('actions.delete')}
                    </Button>
                </div>
            </div>

            <SecurityGroupRulesCard groupId={sg.id} />

            <SecurityGroupModal opened={editOpened} onClose={closeEdit} group={sg} />
            <ConfirmActionModal
                opened={deleteOpened}
                onClose={closeDelete}
                title={t('deleteModal.title')}
                message={t('deleteModal.message', { name: sg.name })}
                confirmLabel={t('deleteModal.confirmLabel')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
        </div>
    );
}
